import { DURATION_OPTIONS } from "../format";
import type { Audience, CalcParams, DayType, DrinkPreference } from "../types";

interface Props {
  params: CalcParams;
  areas: string[];
  onChange: (patch: Partial<CalcParams>) => void;
  onSubmit: () => void;
}

const DAY_TYPES: DayType[] = ["月～木", "金", "土", "日・祝", "祝前"];
const AUDIENCES: Array<{ value: Audience; label: string }> = [
  { value: "一般", label: "一般（大人）" },
  { value: "学生", label: "学生（高校生・大学生）" },
  { value: "シニア", label: "シニア（60歳以上）" },
  { value: "小中学生", label: "小中学生" },
];
const DRINKS: Array<{ value: DrinkPreference; label: string }> = [
  { value: "こだわらない(最安)", label: "こだわらない（最安優先）" },
  { value: "ソフトドリンクバー", label: "ソフトドリンクバー必須" },
  { value: "アルコール飲み放題", label: "アルコール飲み放題必須" },
];

function nowTime(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

export default function SearchForm({ params, areas, onChange, onSubmit }: Props) {
  return (
    <form
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="area">
            エリア
          </label>
          <select
            id="area"
            className="control"
            value={params.area}
            onChange={(event) => onChange({ area: event.target.value })}
          >
            {areas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="day">
            曜日
          </label>
          <select
            id="day"
            className="control"
            value={params.dayType}
            onChange={(event) => onChange({ dayType: event.target.value as DayType })}
          >
            {DAY_TYPES.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="start">
            開始時刻
          </label>
          <div className="flex gap-2">
            <input
              id="start"
              type="time"
              className="control"
              value={params.startTime}
              onChange={(event) => onChange({ startTime: event.target.value })}
            />
            <button
              type="button"
              className="shrink-0 rounded-lg bg-brand-50 px-3 text-xs font-semibold text-brand-600 transition hover:bg-brand-100"
              onClick={() => onChange({ startTime: nowTime() })}
            >
              現在
            </button>
          </div>
          <p className="mt-1 text-[0.68rem] text-slate-500">深夜1:00は「01:00」と入力（前日扱い）</p>
        </div>

        <div>
          <label className="field-label" htmlFor="duration">
            利用時間
          </label>
          <select
            id="duration"
            className="control"
            value={params.durationMinutes}
            onChange={(event) => onChange({ durationMinutes: Number(event.target.value) })}
          >
            {DURATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="people">
            人数
          </label>
          <input
            id="people"
            type="number"
            min={1}
            max={12}
            className="control"
            value={params.numPeople}
            onChange={(event) => {
              const value = Number(event.target.value);
              onChange({ numPeople: Number.isFinite(value) ? Math.min(12, Math.max(1, value)) : 1 });
            }}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="audience">
            客層
          </label>
          <select
            id="audience"
            className="control"
            value={params.audience}
            onChange={(event) => onChange({ audience: event.target.value as Audience })}
          >
            {AUDIENCES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="drink">
            ドリンク
          </label>
          <select
            id="drink"
            className="control"
            value={params.drinkPref}
            onChange={(event) => onChange({ drinkPref: event.target.value as DrinkPreference })}
          >
            {DRINKS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
            checked={params.isMember}
            onChange={(event) => onChange({ isMember: event.target.checked })}
          />
          会員（公式アプリ・カード会員など）
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
            checked={params.includeOptions}
            onChange={(event) => onChange({ includeOptions: event.target.checked })}
          />
          ドリンク代を含める（ワンオーダー・ドリンクバー）
        </label>
      </div>

      <p className="mt-1 text-[0.68rem] text-slate-500">
        30分プランは時間帯をまたぐ場合、各コマの料金で計算します。
      </p>

      <button
        type="submit"
        className="mt-4 w-full rounded-xl bg-brand-500 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600 active:scale-[0.99]"
      >
        最安をさがす
      </button>
    </form>
  );
}
